import { AdvancedSelect } from '@orobix/orobix-fe-components'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Dataset, SelectOption } from './types'
import useFetchDatasets from './useFetchDatasets'

export interface DatasetSelectProps {
  name: string
  productId: number
  value: readonly SelectOption[]
  onChange: (v: readonly SelectOption[]) => void
  onBlur?: () => void
  defaultValue?: readonly SelectOption[]
  isDisabled?: boolean
  error?: boolean
  errorMessage?: string
  label?: string
  placeholder?: string
  noOptionsMessage?: () => string
  excludeTrainingId?: number
  /** IDs string[] to resolve into SelectOption[] after datasets load (used by DatasetFilter). */
  initialValueIds?: string[]
}

const toOption = (d: Dataset): SelectOption => ({
  value: String(d.id),
  label: d.name,
})

const DatasetSelect = ({
  name,
  productId,
  value,
  onChange,
  onBlur,
  defaultValue,
  isDisabled = false,
  error,
  errorMessage,
  label,
  placeholder,
  noOptionsMessage,
  excludeTrainingId,
  initialValueIds,
}: DatasetSelectProps) => {
  const { t } = useTranslation()

  const { datasets, isFetching, refetch } = useFetchDatasets(productId, {
    excludeTrainingId,
  })

  // Resolve string IDs into SelectOption[] once datasets are loaded.
  // Used by DatasetFilter which stores filters as string IDs, not options.
  useEffect(() => {
    if (initialValueIds && initialValueIds.length > 0 && datasets) {
      const resolved = initialValueIds
        .map((id) => datasets.find((d) => String(d.id) === id))
        .filter((d): d is Dataset => d !== undefined)
        .map(toOption)
      if (resolved.length > 0) {
        onChange(resolved)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasets])

  const options = datasets ? datasets.map(toOption) : []

  return (
    <div id={`div${name.charAt(0).toUpperCase() + name.slice(1)}Id`}>
      <AdvancedSelect
        name={name}
        id={name}
        isClearable
        isMulti
        label={label ?? t('Dataset')}
        placeholder={placeholder ?? t('Select...')}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        isLoading={isFetching}
        isDisabled={isDisabled}
        options={options}
        error={error}
        errorMessage={errorMessage}
        noOptionsMessage={noOptionsMessage ?? (() => t('No dataset found'))}
        onMenuOpen={refetch}
      />
    </div>
  )
}

export default DatasetSelect
